/**
 * ComposeShell — the shared floating card chrome for all "compose" panels.
 *
 * This is the entity-agnostic container extracted from the note composer so
 * that any markdown-backed entity (notes, email, …) shares one floating panel:
 * Gmail-style bottom-right card, slideout-aware right offset, entrance
 * animation, and — critically — the stacking/overlay escape hatches that keep
 * inner popovers, modals, tooltips, and the parent slideout behaving correctly.
 *
 * Callers supply the body (a MemoForm, an email compose form, …) as children
 * and a `dataInstance` describing which composer this is (for E2E handles).
 * The shell itself holds NO entity-specific logic.
 */

import type { CSSProperties, ReactNode } from 'react';

import { useOverlayOrder } from '@/contexts/OverlayOrderContext';
import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';
import {
  StackingLayer,
  StackingLevelProvider,
  useStackingZIndex,
} from '@/contexts/StackingContext';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { cx } from '@/utils/cx';

interface ComposeShellProps {
  /**
   * E2E instance handle for this composer, e.g. `note` or `email`. Rendered as
   * `data-instance` alongside `data-component="compose-panel"`.
   */
  dataInstance: string;
  children: ReactNode;
}

export function ComposeShell({ dataInstance, children }: ComposeShellProps) {
  // Register in the shared overlay-order system so the panel — and every modal,
  // tooltip, or popover opened from within it — always stacks above the
  // slideout it was opened from. A *fixed* z-index (the old approach) loses to
  // a slideout that registered a higher open-order level, since slideouts use
  // `useOverlayOrder` too. Registering here gives the composer a level above
  // whatever is currently on top; `StackingLayer` then lifts its children one
  // tier higher still so their popovers/modals sit above the panel itself.
  const level = useOverlayOrder('compose');

  return (
    <StackingLevelProvider level={level}>
      <ComposeShellFrame dataInstance={dataInstance}>
        {children}
      </ComposeShellFrame>
    </StackingLevelProvider>
  );
}

function ComposeShellFrame({ dataInstance, children }: ComposeShellProps) {
  const { stack } = useSlideoutStack();
  const { width: slideoutWidth } = useSlideoutWidth();
  const isSlideoutOpen = stack.length > 0;
  const rightOffset = isSlideoutOpen ? slideoutWidth + 16 : 16;
  // z-index for the panel at its registered overlay-order level.
  const zIndex = useStackingZIndex();

  return (
    <div
      data-component='compose-panel'
      data-instance={dataInstance}
      // `data-keep-overlay-open` keeps the parent slideout from closing on
      // click-outside while interacting with this panel (see slideout-menu.tsx).
      data-keep-overlay-open
      // `data-react-aria-top-layer` prevents react-aria's ariaHideOutside from
      // marking this portaled panel inert when it renders above a slideout/modal.
      data-react-aria-top-layer
      style={
        {
          '--compose-right': `${rightOffset}px`,
          zIndex,
        } as CSSProperties
      }
      className={cx(
        'fixed',
        'bottom-0 left-0 right-0 rounded-t-xl',
        'sm:bottom-4 sm:left-auto sm:right-[var(--compose-right)] sm:w-[560px] sm:rounded-xl',
        'flex flex-col h-[90vh] max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-6rem)]',
        'bg-bg-primary shadow-2xl ring-1 ring-[var(--color-border-secondary)]',
        'animate-in slide-in-from-bottom-4 fade-in duration-200 ease-out'
      )}
    >
      {/* Lift child popovers, modals, and tooltips one tier above the panel so
          their content isn't hidden behind it. */}
      <StackingLayer>
        <div className='flex-1 min-h-0 overflow-y-auto flex flex-col'>
          {children}
        </div>
      </StackingLayer>
    </div>
  );
}
